import { useState } from 'react'
import { Pencil } from 'lucide-react'
import { AddSourceDialog } from './AddSourceDialog'
import { EditSourceDialog } from './EditSourceDialog'
import type { IntegrationSource } from '../types'

type Props = {
  sources: IntegrationSource[]
  isLoading: boolean
  selectedCode: string | null
  onSelect: (code: string) => void
}

/** Lewy panel listy źródeł integracji (wzór: TrackListPanel z Torów Statusów). */
export function SourceListPanel({ sources, isLoading, selectedCode, onSelect }: Props) {
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<IntegrationSource | null>(null)

  return (
    <aside className="w-72 border-r bg-slate-50 flex flex-col">
      <header className="px-3 py-2 border-b flex items-center justify-between">
        <span className="text-xs uppercase text-slate-500">Źródła</span>
        <button
          onClick={() => setCreating(true)}
          className="text-xs px-2 py-1 border rounded bg-white hover:bg-slate-100"
        >
          + Dodaj
        </button>
      </header>

      <div className="flex-1 overflow-auto">
        {isLoading && <p className="p-3 text-xs text-slate-500">Wczytywanie...</p>}
        {!isLoading && sources.length === 0 && (
          <p className="p-3 text-xs text-slate-500 italic">Brak skonfigurowanych źródeł.</p>
        )}
        {sources.map(s => (
          <div
            key={s.code}
            className={[
              'flex items-center justify-between px-3 py-2 border-b cursor-pointer text-sm',
              selectedCode === s.code ? 'bg-white border-l-4 border-l-sky-600' : 'hover:bg-white',
            ].join(' ')}
            onClick={() => onSelect(s.code)}
          >
            <div className="min-w-0">
              <div className="truncate flex items-center gap-2">
                {s.name}
                {!s.isActive && (
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500 shrink-0">
                    nieaktywne
                  </span>
                )}
              </div>
              <div className="text-[10px] font-mono text-slate-500">{s.code} · {s.authType}</div>
            </div>
            <button
              onClick={e => { e.stopPropagation(); setEditing(s) }}
              className="shrink-0 px-1 text-slate-500 hover:text-slate-800"
              aria-label={`Edytuj ${s.code}`}
            >
              <Pencil size={13} />
            </button>
          </div>
        ))}
      </div>

      {creating && <AddSourceDialog onClose={() => setCreating(false)} />}
      {editing && <EditSourceDialog source={editing} onClose={() => setEditing(null)} />}
    </aside>
  )
}
